import { parseAsArrayOf, parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs';

import type { Item, ProductCategoryFilter } from 'types';
import productCategories, { CategoryDto } from 'components/Collection/pages/categories';
import { colorNames, type ColorFilter } from 'partials/Visualizer';
import { combinePredicatesUsingOr } from 'utils/utils';
import { isDavidsBridalProduct } from 'constants/collections';

type UseCollectionFiltersProps = {
  activeCategory: CategoryDto;
  products: Item[];
};

export const useCollectionFilters = ({ products, activeCategory }: UseCollectionFiltersProps) => {
  const getFiltersMatchingActiveCategory = (categories: CategoryDto[]): ProductCategoryFilter[] => {
    const matchingCategory = categories.find((category) => category.displayName.includes(activeCategory.displayName));
    return matchingCategory ? matchingCategory.categoryFilters : [];
  };

  const [categoryFilters, setCategoryFilters] = useQueryState<ProductCategoryFilter[]>('type', {
    defaultValue: [],
    clearOnDefault: true,
    parse: (query: string) => {
      const selectableCategories = getFiltersMatchingActiveCategory(productCategories);
      const selectedCategories = query.split(',');

      return selectableCategories.filter((category) => selectedCategories.includes(category.displayName));
    },
    serialize: (categories) => {
      const categoryNames = categories.map((category) => category.displayName);

      return categoryNames.join(',');
    },
    eq: (currentValue, _defaultValue) => currentValue.length === 0,
  });

  const [colorFilters, setColorFilters] = useQueryState<ColorFilter[]>(
    'color',
    parseAsArrayOf(parseAsStringLiteral(colorNames)).withDefault([]).withOptions({ clearOnDefault: true }),
  );

  const [patternFilters, setPatternFilters] = useQueryState(
    'pattern',
    parseAsArrayOf(parseAsString).withDefault([]).withOptions({ clearOnDefault: true }),
  );

  const [procurementFilters, setProcurementFilters] = useQueryState(
    'procurement',
    parseAsArrayOf(parseAsString).withDefault([]).withOptions({ clearOnDefault: true }),
  );

  const [tieTypeFilters, setTieTypeFilters] = useQueryState(
    'tieType',
    parseAsArrayOf(parseAsString).withDefault([]).withOptions({ clearOnDefault: true }),
  );

  const [davidsBridalFilters, setDavidsBridalFilters] = useQueryState(
    'davidsBridal',
    parseAsArrayOf(parseAsString).withDefault([]).withOptions({ clearOnDefault: true }),
  );

  const getFilteredProducts = () => {
    const filtered = products.filter((product) => {
      if (categoryFilters.length > 0) {
        const combinedPredicates = combinePredicatesUsingOr(categoryFilters.map((filter) => filter.value));
        const values = [product.display_name!];

        /**
         * @todo revisit this along with the category building logic in FilterComponent
         */
        if (product.category !== 'Tie' && product.category !== 'Shirt') values.push(product.category!);
        if (product.category === 'preconfigured' && product.type) values.push(product.type);
        if (!values.some(combinedPredicates)) return false;
      }

      if (patternFilters.length > 0 && !patternFilters.includes(product.pattern!)) return false;
      if (colorFilters.length > 0 && !colorFilters.includes(product.color as ColorFilter)) return false;
      if (tieTypeFilters.length > 0 && !tieTypeFilters.includes(product.attributeSecondary!)) return false;
      if (davidsBridalFilters.length > 0 && !isDavidsBridalProduct(product)) return false;

      if (procurementFilters.length === 1) {
        const lowerCaseProcurementFilters = procurementFilters.map((filter) => filter.toLowerCase());

        if (lowerCaseProcurementFilters.includes('buy')) {
          if (!(product.is_retail || product.retail_bundle?.displayable || product.type?.toLowerCase() === 'purchase')) {
            return false;
          }
        }

        if (lowerCaseProcurementFilters.includes('rent')) {
          if (!(product.is_retail === false || product.type?.toLowerCase() === 'rental')) return false;
        }
      }

      return true;
    });

    const sortByDisplayIndex = (prods: Item[]) =>
      [...prods].sort((a, b) => Number(a.display_index ?? 999) - Number(b.display_index ?? 999));

    const sortPocketSquares = (prods: Item[]) =>
      [...prods].sort((a, b) => {
        const haveMatchingCategories = a.category === b.category;

        if (a.category === 'Pocket Square' && !haveMatchingCategories) return -1;
        if (b.category === 'Pocket Square' && !haveMatchingCategories) return 1;

        return 0;
      });

    let sortedProducts = sortByDisplayIndex(filtered);

    if (activeCategory.name.includes('Pocket Square')) {
      sortedProducts = sortPocketSquares(sortedProducts);
    }

    return sortedProducts;
  };

  const filteredProducts = getFilteredProducts();

  const toggleCategoryFilters = (categoryFilter: ProductCategoryFilter) => {
    setCategoryFilters((prev) =>
      prev.some((f) => f.displayName === categoryFilter.displayName)
        ? prev.filter((filter) => filter.displayName !== categoryFilter.displayName)
        : [...prev, categoryFilter],
    );
  };

  const toggleColorFilters = (colorFilter: ColorFilter) => {
    setColorFilters((prev) => (prev.includes(colorFilter) ? prev.filter((c) => c !== colorFilter) : [...prev, colorFilter]));
  };

  const togglePatternFilters = (patternFilter: string) => {
    setPatternFilters((prev) => (prev.includes(patternFilter) ? prev.filter((p) => p !== patternFilter) : [...prev, patternFilter]));
  };

  const toggleProcurementFilters = (procurementFilter: string) => {
    setProcurementFilters((prev) =>
      prev.includes(procurementFilter) ? prev.filter((r) => r !== procurementFilter) : [...prev, procurementFilter],
    );
  };

  const toggleTieTypesFilters = (tieTypeFilter: string) => {
    setTieTypeFilters((prev) =>
      prev.includes(tieTypeFilter) ? prev.filter((filter) => filter !== tieTypeFilter) : [...prev, tieTypeFilter],
    );
  };

  const toggleDavidsBridalFilters = (davidsBridalFilter: string) => {
    setDavidsBridalFilters((prev) =>
      prev.includes(davidsBridalFilter) ? prev.filter((filter) => filter !== davidsBridalFilter) : [...prev, davidsBridalFilter],
    );
  };

  return {
    filteredProducts,
    categoryFilters,
    colorFilters,
    patternFilters,
    procurementFilters,
    tieTypeFilters,
    davidsBridalFilters,
    toggleCategoryFilters,
    toggleColorFilters,
    togglePatternFilters,
    toggleProcurementFilters,
    toggleTieTypesFilters,
    toggleDavidsBridalFilters,
  };
};
